import React from 'react';
import Swiper from 'swiper';
import type { SwiperOptions } from 'swiper/types';
import { Navigation, Pagination, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';

export interface MPFCardCarouselSlide {
  /** Chave estável da lista — o índice não serve, os itens são reordenáveis. */
  key: string;
  content: React.ReactNode;
}

export interface MPFCardCarouselLabels {
  prev: string;
  next: string;
  first: string;
  last: string;
  /** Precisa conter `{{index}}`: é o Swiper que interpola. */
  bullet: string;
}

export interface MPFCardCarouselProps {
  /**
   * Sufixo das classes de controle (`mpf-swiper-{name}`). PRECISA ser único
   * por carrossel na página: dois carrosséis com o mesmo nome disputariam as
   * mesmas setas e a mesma paginação.
   */
  name: string;
  slides: MPFCardCarouselSlide[];
  slidesPerView?: number;
  spaceBetween?: number;
  breakpoints?: SwiperOptions['breakpoints'];
  /**
   * Posição vertical das setas, como classe utilitária. `top-1/2` quando o
   * card é só a foto; algo como `top-[35%]` quando há legenda abaixo dela e o
   * meio da foto não coincide com o meio do card.
   */
  arrowTopClassName?: string;
  /**
   * Setas laterais. Ligadas por padrão; desligue onde a navegação é por toque
   * e os bullets bastam (o carrossel mobile da faixa "Onde o MPF atua?", por
   * exemplo). Quando `false`, o módulo `Navigation` nem entra — não fica
   * botão escondido por CSS na ordem de tabulação.
   */
  showArrows?: boolean;
  labels: MPFCardCarouselLabels;
  className?: string;
}

/**
 * Carrossel de cards, na API imperativa do Swiper.
 *
 * Mesmas travas do `View/CapaView/OndeAtuaCarousel.tsx`, de onde este
 * componente foi modelado: guarda de SSR, trava contra o duplo-monte do
 * StrictMode e `querySelector` escopado ao próprio nó (nunca `document`).
 *
 * Sem autoplay: as faixas da sub-home são prateleiras de conteúdo, não
 * banners — carrossel que anda sozinho atrapalha quem está lendo. Quem quer
 * troca automática é o banner do topo (`Blocks/HomeBanner`, em Glide).
 *
 * O `OndeAtuaCarousel` da capa NÃO foi migrado para cá de propósito: a capa
 * está em produção e a desduplicação não paga o risco de regressão. Se um dia
 * for unificado, é aqui que ele encaixa.
 */
/**
 * Quem pediu menos animação no sistema não recebe o deslize entre os cards.
 *
 * A consulta é feita ao montar o Swiper (e não numa media query no CSS)
 * porque o que precisa ser desligado é a duração da transição, que é um
 * parâmetro dele — mesma decisão de `View/CapaView/DestaquesCarrossel.tsx`.
 * A navegação continua inteira: as setas e os bullets levam ao próximo card,
 * só que sem o percurso animado.
 */
const prefereMenosMovimento = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const MPFCardCarousel: React.FC<MPFCardCarouselProps> = ({
  name,
  slides,
  slidesPerView = 1,
  spaceBetween = 16,
  breakpoints,
  arrowTopClassName = 'top-1/2',
  showArrows = true,
  labels,
  className,
}) => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const swiperRef = React.useRef<Swiper | null>(null);
  const initialized = React.useRef(false);

  const root = `mpf-swiper-${name}`;
  const count = slides.length;

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (initialized.current) return undefined;
    const el = rootRef.current?.querySelector<HTMLElement>(`.${root}`);
    if (!el) return undefined;

    initialized.current = true;
    swiperRef.current = new Swiper(el, {
      modules: showArrows ? [Navigation, Pagination, A11y] : [Pagination, A11y],
      slidesPerView,
      spaceBetween,
      // DESLIGADO, e é decisão de correção, não de estilo.
      //
      // Com `loop`, o Swiper CLONA slides dentro do `.swiper-wrapper` — nós
      // que o React não conhece, num container cujos filhos ele mesmo
      // renderiza. Na leitura isso passa despercebido (o componente quase não
      // re-renderiza), mas no editor cada tecla digitada no formulário
      // dispara um render: os clones se acumulavam, o `resizeObserver`
      // enxergava o container mudar de tamanho, mandava o Swiper reconstruir
      // o loop, e o ciclo se realimentava — a faixa crescia sem parar e
      // empurrava para fora da tela os blocos abaixo dela.
      //
      // Sem loop, o React é o dono exclusivo do DOM dos slides e o Swiper só
      // mede e posiciona. O preço é a navegação parar nas pontas, que o
      // `.swiper-button-disabled` já trata escondendo a seta — mesmo
      // comportamento do carrossel da capa quando há poucos cards.
      loop: false,
      speed: prefereMenosMovimento() ? 0 : 500,
      // Sem as mensagens, o módulo de a11y sobrescreve o `aria-label` dos
      // nossos botões com os textos padrão dele, em inglês.
      a11y: {
        enabled: true,
        prevSlideMessage: labels.prev,
        nextSlideMessage: labels.next,
        firstSlideMessage: labels.first,
        lastSlideMessage: labels.last,
        paginationBulletMessage: labels.bullet,
      },
      // LIGADO, ao contrário do `OndeAtuaCarousel` da capa.
      //
      // Na sub-home a coluna de conteúdo encolhe DEPOIS da montagem:
      // `useHasSidebar` só sabe se a página tem sidebar de slot quando
      // `state.slots.data` chega do cliente, e até lá a coluna ocupa 12/12;
      // ao virar `--centered` passa a 8/12. Sem observar, o Swiper fica com a
      // medida da criação — 1216px para um trilho que virou 792px — e
      // dimensiona os slides grandes demais: o carrossel configurado para 3
      // mostra 2. Como é uma corrida com a resposta do servidor, o sintoma
      // aparece e some conforme a máquina e a rede.
      //
      // Na capa isso não acontece: lá a faixa nasce e permanece na mesma
      // largura, e por isso o carrossel de lá pode economizar o observer.
      //
      // Só é seguro porque o `loop` acima está desligado: era a reconstrução
      // dos clones a cada `update()` que fechava o ciclo de realimentação no
      // editor. Sem clones, `update()` mede e posiciona, mas não mexe no DOM.
      resizeObserver: true,
      breakpoints,
      ...(showArrows
        ? {
            navigation: {
              prevEl: el.querySelector<HTMLElement>(`.${root}-prev`),
              nextEl: el.querySelector<HTMLElement>(`.${root}-next`),
            },
          }
        : {}),
      pagination: {
        el: el.querySelector<HTMLElement>(`.${root}-pagination`),
        clickable: true,
      },
    });

    return () => {
      swiperRef.current?.destroy(true, true);
      swiperRef.current = null;
      initialized.current = false;
    };
    // `count` é a única dependência que precisa remontar o Swiper: as demais
    // props são estáticas por carrossel. Trocar a lista sem trocar o tamanho
    // é resolvido pelo `key` que o chamador põe no componente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  if (count === 0) return null;

  const arrowClass = `mpf-card-carousel__arrow ${arrowTopClassName}`;

  return (
    /* `w-full min-w-0`: o wrapper não pode herdar a largura do conteúdo, ou
       o trilho empurra o pai e realimenta o `resizeObserver` — ver a trava em
       `.mpf-card-carousel`, no SCSS. */
    <div ref={rootRef} className={`w-full min-w-0 ${className ?? ''}`}>
      <div className={`swiper mpf-card-carousel ${root} relative`}>
        <div className="swiper-wrapper">
          {slides.map((slide) => (
            /* `h-auto`: sem isso o Swiper iguala a altura dos slides pelo
               maior, e o card do meio ganha um vão embaixo da legenda. */
            <div
              key={slide.key}
              className="swiper-slide h-auto"
              suppressHydrationWarning
            >
              {slide.content}
            </div>
          ))}
        </div>

        {showArrows && (
          <>
            <button
              type="button"
              aria-label={labels.prev}
              data-tooltip={labels.prev}
              className={`${root}-prev left-0 ${arrowClass}`}
            >
              <CaretLeftIcon aria-hidden="true" className="h-5 w-5" />
            </button>
            <button
              type="button"
              aria-label={labels.next}
              data-tooltip={labels.next}
              className={`${root}-next right-0 ${arrowClass}`}
            >
              <CaretRightIcon aria-hidden="true" className="h-5 w-5" />
            </button>
          </>
        )}

        <div
          className={`${root}-pagination mpf-card-carousel__pagination mt-4 flex justify-center gap-4`}
        />
      </div>
    </div>
  );
};

export default MPFCardCarousel;
